import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { ArrowLeftIcon, LayoutDashboardIcon, ClockIcon, ArchiveIcon, PowerIcon, TrendingUpIcon, LockIcon, ShieldIcon, BellIcon, BellOffIcon, QrCodeIcon, PackageIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Order, OrderStatus, DashboardStatsData } from './types';
import { DashboardStats } from './DashboardStats';
import { OrdersTable } from './OrdersTable';
import { KitchenTicketView } from './KitchenTicketView';
import { OrderDetailModal } from './OrderDetailModal';
import { AnalyticsPanel } from './AnalyticsPanel';
import { InventoryPanel } from './inventory/InventoryPanel';
import { CONFIG } from '../data/config';
import { Station, STATIONS, orderHasItemsForStation, countStationItems } from './StationConfig';
import { OpenTablesPanel } from './OpenTablesPanel';
import { TableMapPanel } from './TableMapPanel';
interface RestaurantDashboardProps {
  onBack: () => void;
}
const QUOTES = ['"El éxito no es un accidente. Es trabajo duro, perseverancia y amor por lo que haces."', '"La calidad de tu vida es la calidad de tus decisiones." — Tony Robbins', '"No esperes el momento perfecto. Toma el momento y hazlo perfecto."', '"Cada día es una nueva oportunidad para ser extraordinario."', '"El secreto del éxito es hacer lo que otros no están dispuestos a hacer."', '"Tu actitud determina tu altitud." — Alex Dey'];
export function RestaurantDashboard({
  onBack
}: RestaurantDashboardProps) {
  // Always start at Welcome Screen — no session restoration
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticatedStation, setAuthenticatedStation] = useState<Station | null>(null);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === CONFIG.ADMIN_PIN) {
      setIsAuthenticated(true);
      setAuthenticatedStation(selectedStation);
      sessionStorage.setItem('kds_auth', 'true');
      if (selectedStation) {
        sessionStorage.setItem('kds_station', selectedStation);
        setActiveStation(selectedStation);
        window.location.hash = selectedStation;
      }
      setPinError(false);
    } else {
      setPinError(true);
      setPin('');
    }
  };
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('kds_sound') !== 'false';
  });
  const knownOrderIds = useRef<Set<string>>(new Set());
  const isFirstLoad = useRef(true);
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      // Play two tones for a pleasant "ding-dong" chime
      const playTone = (freq: number, startTime: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0.3, startTime);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      const now = ctx.currentTime;
      playTone(830, now, 0.15);
      playTone(1100, now + 0.15, 0.3);
      playTone(830, now + 0.5, 0.15);
      playTone(1100, now + 0.65, 0.3);
      // Cleanup
      setTimeout(() => ctx.close(), 2000);
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }, [soundEnabled]);
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('kds_sound', String(next));
      return next;
    });
  };
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [stats, setStats] = useState<DashboardStatsData>({
    ordersToday: 0,
    ordersLastHour: 0,
    salesToday: 0,
    averageTicket: 0
  });
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<'active' | 'history' | 'reports' | 'inventory'>('active');
  const [isAnalyticsExpanded, setIsAnalyticsExpanded] = useState(false);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  const [activeStation, setActiveStation] = useState<Station>(() => {
    const saved = sessionStorage.getItem('kds_station') as Station;
    if (saved && ['caja', 'cocina', 'pizzeria', 'bebidas'].includes(saved)) return saved;
    const hash = window.location.hash.replace('#', '');
    if (['caja', 'cocina', 'pizzeria', 'bebidas'].includes(hash)) return hash as Station;
    return 'caja';
  });
  // Per-station status tracking — independent of main order status
  const [stationStatuses, setStationStatuses] = useState<Record<string, OrderStatus>>(() => {
    try {
      const saved = localStorage.getItem('kds_station_statuses');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {};
  });
  useEffect(() => {
    try {
      localStorage.setItem('kds_station_statuses', JSON.stringify(stationStatuses));
    } catch (e) {}
  }, [stationStatuses]);
  const getStationStatus = useCallback((orderId: string, station: Station): OrderStatus | null => {
    return stationStatuses[`${orderId}_${station}`] || null;
  }, [stationStatuses]);
  const setStationStatus = useCallback((orderId: string, station: Station, status: OrderStatus) => {
    setStationStatuses(prev => ({
      ...prev,
      [`${orderId}_${station}`]: status
    }));
  }, []);
  const isOrderDoneForStation = useCallback((order: Order, station: Station): boolean => {
    if (station === 'caja') return ['delivered', 'cancelled'].includes(order.status);
    const stationStatus = getStationStatus(order.id, station);
    if (stationStatus && ['delivered', 'cancelled'].includes(stationStatus)) return true;
    return ['delivered', 'cancelled'].includes(order.status);
  }, [getStationStatus]);
  const getEffectiveStatus = useCallback((order: Order, station: Station): OrderStatus => {
    if (station === 'caja') return order.status;
    return getStationStatus(order.id, station) || order.status;
  }, [getStationStatus]);
  const activeOrders = useMemo(() => orders.filter(o => !['delivered', 'cancelled'].includes(o.status)), [orders]);
  const historyOrders = useMemo(() => orders.filter(o => ['delivered', 'cancelled'].includes(o.status)), [orders]);
  const displayedOrders = useMemo(() => {
    if (activeStation === 'caja') {
      return activeTab === 'active' ? activeOrders : historyOrders;
    }
    // Non-caja stations: ONLY show orders that caja has accepted (not "new")
    const acceptedByRestaurant = orders.filter(o => o.status !== 'new' && orderHasItemsForStation(o.order_text, activeStation));
    if (activeTab === 'active') {
      return acceptedByRestaurant.filter(o => !isOrderDoneForStation(o, activeStation));
    } else {
      return acceptedByRestaurant.filter(o => isOrderDoneForStation(o, activeStation));
    }
  }, [activeTab, activeOrders, historyOrders, activeStation, orders, isOrderDoneForStation]);
  const fetchSettings = useCallback(async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_settings').select('kitchen_paused').eq('id', 1).single();
      if (error) {
        console.error('Error fetching settings:', error);
        return;
      }
      if (data) {
        setKitchenPaused(data.kitchen_paused);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  }, []);
  const toggleKitchen = async () => {
    setTogglingKitchen(true);
    try {
      const newValue = !kitchenPaused;
      const {
        error
      } = await supabase.from('restaurant_settings').update({
        kitchen_paused: newValue,
        updated_at: new Date().toISOString()
      }).eq('id', 1);
      if (error) throw error;
      setKitchenPaused(newValue);
    } catch (err) {
      console.error('Error toggling kitchen:', err);
      alert('Error al actualizar el estado de la cocina. Verifica que la tabla restaurant_settings exista.');
    } finally {
      setTogglingKitchen(false);
    }
  };
  // Read local orders from localStorage (fallback when Supabase fails)
  const getLocalOrders = useCallback((): Order[] => {
    try {
      const raw = localStorage.getItem('kds_local_orders');
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Order[];
      // Apply any local status overrides to local orders too
      return parsed.map(o => {
        const override = localStatusOverrides.current.get(o.id);
        return override ? {
          ...o,
          status: override
        } : o;
      });
    } catch (e) {
      return [];
    }
  }, []);
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('orders').select('*').order('created_at', {
        ascending: false
      }).limit(1000);
      let supabaseOrders: Order[] = [];
      if (error) {
        console.error('Error fetching orders from Supabase:', error);
      } else if (data) {
        supabaseOrders = data as Order[];
      }
      // Merge with localStorage orders (dedup by order_number)
      const localOrders = getLocalOrders();
      const supabaseOrderNumbers = new Set(supabaseOrders.map(o => o.order_number));
      const uniqueLocalOrders = localOrders.filter(lo => !supabaseOrderNumbers.has(lo.order_number));
      let newOrders = [...supabaseOrders, ...uniqueLocalOrders].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      // Apply local status overrides
      if (localStatusOverrides.current.size > 0) {
        newOrders = newOrders.map(o => {
          const override = localStatusOverrides.current.get(o.id);
          return override ? {
            ...o,
            status: override
          } : o;
        });
      }
      // Detect new orders and play sound
      if (isFirstLoad.current) {
        newOrders.forEach(o => knownOrderIds.current.add(o.id));
        isFirstLoad.current = false;
      } else {
        const freshOrders = newOrders.filter(o => !knownOrderIds.current.has(o.id) && o.status === 'new');
        if (freshOrders.length > 0) {
          playNotificationSound();
        }
        newOrders.forEach(o => knownOrderIds.current.add(o.id));
      }
      setOrders(newOrders);
      calculateStats(newOrders);
    } catch (err) {
      console.error('Error:', err);
      // Even if everything fails, try localStorage
      const localOrders = getLocalOrders();
      if (localOrders.length > 0) {
        setOrders(localOrders);
        calculateStats(localOrders);
      }
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);
  const calculateStats = (ordersList: Order[]) => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const todayOrders = ordersList.filter(o => new Date(o.created_at) >= startOfDay && o.status !== 'cancelled');
    const lastHourOrders = ordersList.filter(o => new Date(o.created_at) >= oneHourAgo && o.status !== 'cancelled');
    const salesToday = todayOrders.reduce((sum, o) => sum + (o.total_final || 0), 0);
    const averageTicket = todayOrders.length > 0 ? Math.round(salesToday / todayOrders.length) : 0;
    setStats({
      ordersToday: todayOrders.length,
      ordersLastHour: lastHourOrders.length,
      salesToday,
      averageTicket
    });
  };
  // Local status overrides — persists across refreshes until Supabase confirms
  const localStatusOverrides = useRef<Map<string, OrderStatus>>(new Map());
  const updateOrderStatus = useCallback(async (orderId: string, newStatus: OrderStatus) => {
    // Non-caja stations: only update their station-specific status, NOT the main order
    if (activeStation !== 'caja') {
      setStationStatus(orderId, activeStation, newStatus);
      setSelectedOrder(null);
      return;
    }
    // When Caja accepts, initialize all relevant stations to "accepted"
    if (newStatus === 'accepted') {
      const order = orders.find(o => o.id === orderId);
      if (order) {
        const stationsForOrder: Station[] = ['cocina', 'pizzeria', 'bebidas'];
        stationsForOrder.forEach(st => {
          if (orderHasItemsForStation(order.order_text, st)) {
            // Only set if station doesn't already have a status
            if (!getStationStatus(orderId, st)) {
              setStationStatus(orderId, st, 'accepted');
            }
          }
        });
      }
    }
    // Caja: update the real order status in DB
    // Immediately update local state and save override
    localStatusOverrides.current.set(orderId, newStatus);
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      status: newStatus
    } : o));
    // Update selected order if open
    setSelectedOrder(prev => prev && prev.id === orderId ? {
      ...prev,
      status: newStatus
    } : prev);
    // Recalculate stats
    setOrders(prev => {
      calculateStats(prev);
      return prev;
    });
    // Try to persist to Supabase
    const {
      error
    } = await supabase.from('orders').update({
      status: newStatus
    }).eq('id', orderId);
    if (error) {
      console.error('⚠️ Error updating status in Supabase:', error);
      // Keep local override active — will be re-applied on refresh
    } else {
      // Supabase confirmed — remove override (DB is now in sync)
      localStatusOverrides.current.delete(orderId);
    }
    // Also update localStorage orders if it's a local order
    if (orderId.startsWith('local_')) {
      try {
        const raw = localStorage.getItem('kds_local_orders');
        if (raw) {
          const localOrders = JSON.parse(raw);
          const updated = localOrders.map((o: any) => o.id === orderId ? {
            ...o,
            status: newStatus
          } : o);
          localStorage.setItem('kds_local_orders', JSON.stringify(updated));
        }
      } catch (e) {}
    }
  }, [activeStation, setStationStatus]);
  // Initial fetch
  useEffect(() => {
    fetchOrders();
    fetchSettings();
  }, [fetchOrders, fetchSettings]);
  // Auto-refresh every 5 seconds for fast order detection
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrders();
      fetchSettings();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders, fetchSettings]);
  // PIN Gate & Welcome Screen
  if (!isAuthenticated) {
    if (!selectedStation) {
      // Step 1: Welcome Screen
      return <div className="min-h-screen bg-carbon w-full flex items-center justify-center px-4 py-8" data-id="element-712">
          <div className="w-full max-w-lg flex flex-col items-center" data-id="element-713">
            {/* Logo */}
            <div className="w-28 h-28 rounded-2xl overflow-hidden border border-[#2A2A2F] shadow-2xl shadow-black/50 mb-6 bg-[#16161A]" data-id="element-714">
              <img src={CONFIG.LOGO_URL} alt={CONFIG.APP_NAME} className="w-full h-full object-contain p-2" data-id="element-715" />
            </div>

            <h1 className="font-heading text-3xl text-white tracking-tight mb-1 text-center" data-id="element-716">
              <span className="noiza-gradient-text" data-id="element-717">{CONFIG.APP_NAME}</span>
            </h1>
            <p className="text-[#A1A1AA] text-sm mb-4" data-id="element-718">{CONFIG.APP_TAGLINE}</p>

            <div className="w-full max-w-md mb-10 pl-4 border-l-2 border-[#00F5C9]/30" data-id="element-719">
              <p className="text-gray-400 text-sm italic" data-id="element-720">{quote}</p>
            </div>

            <div className="w-full" data-id="element-721">
              <h2 className="text-gray-500 text-xs uppercase tracking-wider font-bold mb-4 text-center" data-id="element-722">
                Selecciona tu estación
              </h2>

              <div className="grid grid-cols-2 gap-3 mb-8" data-id="element-723">
                {STATIONS.map(station => <button key={station.id} onClick={() => setSelectedStation(station.id)} className="bg-surface border border-gray-800 rounded-xl p-5 flex flex-col items-center justify-center text-center transition-all hover:-translate-y-1 hover:shadow-xl group" style={{
                '--hover-color': station.id === 'caja' ? '#F2C94C' : station.id === 'cocina' ? '#00F5C9' : station.id === 'pizzeria' ? '#7A5CFF' : '#00F5C9'
              } as React.CSSProperties} data-id="element-724">
                    <style data-id="element-725">{`
                      .group:hover { border-color: var(--hover-color); }
                    `}</style>
                    <span className="text-3xl mb-3" data-id="element-726">{station.icon}</span>
                    <span className="font-bold text-white mb-1" data-id="element-727">
                      {station.label}
                    </span>
                    <span className="text-xs text-gray-500 leading-tight" data-id="element-728">
                      {station.description}
                    </span>
                  </button>)}
              </div>
            </div>

            <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-300 transition-colors" data-id="element-729">
              ← Volver al menú
            </button>
          </div>
        </div>;
    }
    // Step 2: PIN Entry
    const stationDef = STATIONS.find(s => s.id === selectedStation)!;
    return <div className="min-h-screen bg-carbon w-full flex items-center justify-center px-4" data-id="element-730">
        <div className="w-full max-w-sm" data-id="element-731">
          <div className="bg-surface border border-gray-800 rounded-2xl p-8 shadow-2xl relative" data-id="element-732">
            <button onClick={() => {
            setSelectedStation(null);
            setPin('');
            setPinError(false);
          }} className="absolute top-4 left-4 p-2 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-surface-light" data-id="element-733">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-734" />
            </button>

            <div className="flex flex-col items-center mb-8 mt-4" data-id="element-735">
              <div className={`w-16 h-16 ${stationDef.bg} rounded-full flex items-center justify-center border-2 ${stationDef.border} mb-4`} data-id="element-736">
                <span className="text-3xl" data-id="element-737">{stationDef.icon}</span>
              </div>
              <h1 className="font-heading text-2xl text-white tracking-wide" data-id="element-738">
                {stationDef.label}
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-739">
                Ingresa el PIN para acceder
              </p>
            </div>

            <form onSubmit={handlePinSubmit} className="space-y-4" data-id="element-740">
              <div data-id="element-741">
                <input type="password" inputMode="numeric" maxLength={6} value={pin} onChange={e => {
                setPin(e.target.value);
                setPinError(false);
              }} placeholder="PIN de acceso" className={`w-full text-center text-2xl tracking-[0.5em] bg-carbon border ${pinError ? 'border-red-500' : 'border-gray-700'} rounded-xl px-4 py-4 text-white placeholder:text-gray-600 placeholder:tracking-normal placeholder:text-base focus:outline-none focus:border-gray-500 transition-colors`} autoFocus data-id="element-742" />
                {pinError && <p className="text-red-400 text-sm text-center mt-2" data-id="element-743">
                    PIN incorrecto. Intenta de nuevo.
                  </p>}
              </div>

              <button type="submit" disabled={pin.length === 0} className="w-full py-3.5 font-bold rounded-xl transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2 noiza-gradient text-[#0D0D0F] hover:opacity-90" data-id="element-744">
                <LockIcon className="w-4 h-4" data-id="element-745" />
                Acceder
              </button>
            </form>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-carbon w-full" data-id="element-746">
      {/* Top Bar */}
      <header className="bg-surface border-b border-gray-800 sticky top-0 z-30" data-id="element-747">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-748">
          <div className="flex items-center justify-between h-16" data-id="element-749">
            <div className="flex items-center gap-4" data-id="element-750">
              <button onClick={() => {
              sessionStorage.removeItem('kds_auth');
              sessionStorage.removeItem('kds_station');
              setIsAuthenticated(false);
              setAuthenticatedStation(null);
              setSelectedStation(null);
              setPin('');
              window.location.hash = 'admin';
            }} className="p-2 bg-surface-light rounded-lg text-gray-400 hover:text-white transition-colors border border-gray-700" data-id="element-751">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-752" />
              </button>
              <div className="flex items-center gap-3" data-id="element-753">
                <img src={CONFIG.LOGO_URL} alt={CONFIG.APP_NAME} className="w-9 h-9 rounded-lg object-contain" data-id="element-754" />
                <div data-id="element-755">
                  <h1 className="font-heading text-xl text-white tracking-tight leading-none" data-id="element-756">
                    <span className="noiza-gradient-text font-bold" data-id="element-757">
                      {CONFIG.APP_NAME}
                    </span>
                    <span className="text-gray-500 font-normal text-sm ml-2" data-id="element-758">
                      Panel Admin
                    </span>
                  </h1>
                  <p className="text-xs text-gray-500" data-id="element-759">{CONFIG.APP_TAGLINE}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4" data-id="element-760">
              {/* QR Generator — Caja only */}
              {authenticatedStation === 'caja' && <button onClick={() => {
              window.location.hash = '#qr';
              window.location.reload();
            }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border bg-dorado/10 text-dorado border-dorado/20 hover:bg-dorado/20" data-id="element-761">
                  <QrCodeIcon className="w-4 h-4" data-id="element-762" />
                  <span className="hidden sm:inline" data-id="element-763">QR Mesas</span>
                </button>}

              {/* Sound Toggle */}
              <button onClick={() => {
              toggleSound();
              // Play a test sound when enabling
              if (!soundEnabled) {
                setTimeout(() => {
                  try {
                    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(1100, ctx.currentTime);
                    gain.gain.setValueAtTime(0.2, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
                    osc.start();
                    osc.stop(ctx.currentTime + 0.2);
                    setTimeout(() => ctx.close(), 500);
                  } catch (e) {}
                }, 50);
              }
            }} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${soundEnabled ? 'bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20' : 'bg-gray-500/10 text-gray-500 border-gray-600 hover:bg-gray-500/20'}`} data-id="element-764">
                {soundEnabled ? <BellIcon className="w-4 h-4" data-id="element-765" /> : <BellOffIcon className="w-4 h-4" data-id="element-766" />}
                <span className="hidden sm:inline" data-id="element-767">
                  {soundEnabled ? 'Sonido On' : 'Sonido Off'}
                </span>
              </button>

              {/* Kitchen Status Toggle — Caja only */}
              {authenticatedStation === 'caja' && <button onClick={toggleKitchen} disabled={togglingKitchen} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${kitchenPaused ? 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20'} ${togglingKitchen ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-768">
                  <PowerIcon className="w-4 h-4" data-id="element-769" />
                  <span className="hidden sm:inline" data-id="element-770">
                    {kitchenPaused ? 'Cocina Pausada' : 'Cocina Activa'}
                  </span>
                </button>}

              <div className="w-px h-8 bg-gray-800 hidden sm:block" data-id="element-771" />

              <div className="text-right hidden sm:block" data-id="element-772">
                <p className="text-xs text-gray-500" data-id="element-773">Última actualización</p>
                <p className="text-xs text-gray-400" data-id="element-774">
                  {lastRefresh.toLocaleTimeString('es-CR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
                </p>
              </div>
              <div className={`w-2 h-2 rounded-full animate-pulse ${kitchenPaused ? 'bg-red-500' : 'bg-emerald-400'}`} data-id="element-775" />
            </div>
          </div>
        </div>
      </header>

      {/* Station Selector Bar — Only for Caja (full access) */}
      {authenticatedStation === 'caja' ? <div className="bg-surface border-b border-gray-800 sticky top-16 z-20" data-id="element-776">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-777">
            <div className="flex items-center gap-2 py-3 overflow-x-auto no-scrollbar" data-id="element-778">
              {STATIONS.map(station => {
            const isActive = activeStation === station.id;
            const count = station.id === 'caja' ? activeOrders.length : orders.filter(o => orderHasItemsForStation(o.order_text, station.id) && !isOrderDoneForStation(o, station.id) && !['delivered', 'cancelled'].includes(o.status)).length;
            return <button key={station.id} onClick={() => {
              setActiveStation(station.id);
              window.location.hash = station.id;
              if (station.id !== 'caja' && activeTab === 'reports') {
                setActiveTab('active');
              }
            }} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap border ${isActive ? `${station.bg} ${station.color} ${station.border}` : 'bg-surface-light text-gray-400 border-gray-800 hover:bg-gray-800'}`} data-id="element-779">
                    <span className="text-base leading-none" data-id="element-780">
                      {station.icon}
                    </span>
                    {station.label}
                    {count > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ml-1 ${isActive ? 'bg-black/20' : 'bg-gray-700 text-gray-300'}`} data-id="element-781">
                        {count}
                      </span>}
                  </button>;
          })}
            </div>
          </div>
        </div> /* Non-Caja: Show locked station indicator */ : <div className="bg-surface border-b border-gray-800 sticky top-16 z-20" data-id="element-782">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-783">
            <div className="flex items-center justify-between py-3" data-id="element-784">
              <div className="flex items-center gap-2" data-id="element-785">
                {(() => {
              const stationDef = STATIONS.find(s => s.id === authenticatedStation);
              return stationDef ? <span className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border ${stationDef.bg} ${stationDef.color} ${stationDef.border}`} data-id="element-786">
                      <span className="text-base leading-none" data-id="element-787">
                        {stationDef.icon}
                      </span>
                      {stationDef.label}
                    </span> : null;
            })()}
              </div>
              <button onClick={() => {
            sessionStorage.removeItem('kds_auth');
            sessionStorage.removeItem('kds_station');
            setIsAuthenticated(false);
            setAuthenticatedStation(null);
            setSelectedStation(null);
            setPin('');
          }} className="text-xs text-gray-500 hover:text-gray-300 transition-colors px-3 py-1.5 rounded-lg border border-gray-800 hover:border-gray-700" data-id="element-788">
                Cambiar estación
              </button>
            </div>
          </div>
        </div>}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-id="element-789">
        {/* Stats - Only show in Caja */}
        {activeStation === 'caja' && <DashboardStats stats={stats} data-id="element-790" />}

        {/* Table Map - Only show in Caja */}
        {activeStation === 'caja' && <TableMapPanel orders={orders} data-id="element-791" />}

        {/* Open Tables - Only show in Caja */}
        {activeStation === 'caja' && <OpenTablesPanel orders={orders} onRefresh={fetchOrders} onUpdateLocalOrders={setOrders} data-id="element-792" />}

        {/* Tab Navigation — Full tabs for Caja, simplified for other stations */}
        {authenticatedStation === 'caja' ? <div className="flex items-center gap-1 bg-surface rounded-xl p-1 border border-gray-800 w-fit" data-id="element-793">
            <button onClick={() => setActiveTab('active')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'active' ? 'bg-dorado/10 text-dorado border border-dorado/20' : 'text-gray-500 hover:text-gray-300 border border-transparent'}`} data-id="element-794">
              <ClockIcon className="w-4 h-4" data-id="element-795" />
              Pedidos activos
              {activeOrders.length > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'active' ? 'bg-dorado/20 text-dorado' : 'bg-gray-800 text-gray-400'}`} data-id="element-796">
                  {activeOrders.length}
                </span>}
            </button>
            <button onClick={() => setActiveTab('history')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'history' ? 'bg-gray-400/10 text-gray-300 border border-gray-600' : 'text-gray-500 hover:text-gray-300 border border-transparent'}`} data-id="element-797">
              <ArchiveIcon className="w-4 h-4" data-id="element-798" />
              Historial
              {historyOrders.length > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'history' ? 'bg-gray-600 text-gray-300' : 'bg-gray-800 text-gray-500'}`} data-id="element-799">
                  {historyOrders.length}
                </span>}
            </button>
            {activeStation === 'caja' && <button onClick={() => setActiveTab('reports')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'reports' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30' : 'text-gray-500 hover:text-gray-300 border border-transparent'}`} data-id="element-800">
                <TrendingUpIcon className="w-4 h-4" data-id="element-801" />
                Reportes
              </button>}
            {activeStation === 'caja' && <button onClick={() => setActiveTab('inventory')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'inventory' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'text-gray-500 hover:text-gray-300 border border-transparent'}`} data-id="element-802">
                <PackageIcon className="w-4 h-4" data-id="element-803" />
                Inventario
              </button>}
          </div> /* Non-Caja: Just show active orders header */ : <div className="flex items-center gap-2" data-id="element-804">
            <ClockIcon className="w-4 h-4 text-dorado" data-id="element-805" />
            <h2 className="font-heading text-lg text-white" data-id="element-806">Pedidos activos</h2>
            {activeOrders.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-dorado/20 text-dorado" data-id="element-807">
                {activeOrders.length}
              </span>}
          </div>}

        {/* Main View Area */}
        <div data-id="element-808">
          {activeTab === 'inventory' ? <InventoryPanel data-id="element-809" /> : activeTab === 'reports' ? <AnalyticsPanel orders={orders} expanded={isAnalyticsExpanded} onToggleExpand={() => setIsAnalyticsExpanded(!isAnalyticsExpanded)} data-id="element-810" /> : <>
              <div className="flex items-center gap-3 mb-4" data-id="element-811">
                <h2 className="font-heading text-2xl text-white tracking-wide" data-id="element-812">
                  {activeTab === 'active' ? 'Pedidos activos' : 'Historial de pedidos'}
                </h2>
                {activeStation !== 'caja' && <span className={`px-3 py-1 rounded-full text-sm font-bold border ${STATIONS.find(s => s.id === activeStation)?.bg} ${STATIONS.find(s => s.id === activeStation)?.color} ${STATIONS.find(s => s.id === activeStation)?.border}`} data-id="element-813">
                    {STATIONS.find(s => s.id === activeStation)?.icon}{' '}
                    {STATIONS.find(s => s.id === activeStation)?.label}
                  </span>}
              </div>
              {activeStation === 'caja' ? <OrdersTable orders={displayedOrders} loading={loading} onViewDetail={setSelectedOrder} onUpdateStatus={updateOrderStatus} onRefresh={fetchOrders} activeStation={activeStation} getEffectiveStatus={getEffectiveStatus} data-id="element-814" /> : <KitchenTicketView orders={displayedOrders} onUpdateStatus={updateOrderStatus} onViewDetail={setSelectedOrder} activeStation={activeStation} getEffectiveStatus={getEffectiveStatus} data-id="element-815" />}
            </>}
        </div>
      </main>

      {/* Order Detail Modal */}
      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} onUpdateStatus={updateOrderStatus} activeStation={activeStation} isAdditional={(() => {
      if (!selectedOrder.table_number) return false;
      const openStatuses = new Set(['new', 'accepted', 'preparing', 'ready']);
      if (!openStatuses.has(selectedOrder.status)) return false;
      const sameTableOrders = orders.filter(o => o.table_number === selectedOrder.table_number && openStatuses.has(o.status)).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      return sameTableOrders.length > 1 && sameTableOrders[0].id !== selectedOrder.id;
    })()} data-id="element-816" />}
    </div>;
}